import type { WrapperFC } from '@george-labs.com/design-system';
import { useRef } from 'react';
import { OverviewCardMenu } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import type { ProductWithHelpers } from '@shared/products-core';
import type { CopyProductNumberAction } from '@retail/overview-utils';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { useNotifications } from '@shared/snackbar';

import type { DropdownEntry } from '../../overview-actions-dropdown/types';
import { AliasChange as ExternalPartialPAPIAliasChange } from '../personalization-card/customizations/AliasChange';
import { ColorChange as ExternalPartialPAPIColorChange } from '../personalization-card/customizations/ColorChange';
import { ProductImageUpload as ExternalPartialPAPIImageChange } from '../personalization-card/components/ProductImageUpload';
import { useDashboardAppearanceChange as useExternalPartialPAPIDashboardAppearanceChange } from '../personalization-card/customizations/hooks/useDashboardAppearanceChange';
import { CardMenuDropdownToggle } from '../../overview-actions-dropdown/CardMenuDropdownToggle';
import { useDropdownEntries } from '../../overview-actions-dropdown/useDropdownEntries';
import {
    getDropdownActions,
    getRegisteredDropdownActions,
} from '../../overview-actions-dropdown/ActionsDropdownService';

export interface ActionsDropdownContentProps {
    useRegisteredItems?: boolean;
    isAliasChangeAllowed?: boolean;
    copyProductNumberAction?: CopyProductNumberAction;
    settingsUrl?: string;
    account: ProductWithHelpers<Account>;
}

export const ActionsDropdownContent: WrapperFC<ActionsDropdownContentProps> = ({
    useRegisteredItems = false,
    isAliasChangeAllowed = true,
    copyProductNumberAction,
    settingsUrl,
    account,
}) => {
    const { displaySuccess } = useNotifications('overview-dropdown-actions');
    const {
        isAliasChangeCustomizationActive,
        isColorChangeCustomizationActive,
        activateAliasChangeCustomization,
        activateColorChangeCustomization,
    } = useActiveCustomizationFeature();
    const { hideAccountOnOverview } = useExternalPartialPAPIDashboardAppearanceChange();
    const imageUploadInputRef = useRef<HTMLInputElement>(null);

    const dropdownActions = useRegisteredItems
        ? getRegisteredDropdownActions(
              account,
              isAliasChangeAllowed,
              copyProductNumberAction,
              settingsUrl,
          )
        : getDropdownActions(account, isAliasChangeAllowed);

    const { dropdownEntries, performEntryAction } = useDropdownEntries(
        dropdownActions,
        displaySuccess,
    );

    if (!dropdownEntries.length) {
        return null;
    }

    if (isColorChangeCustomizationActive) {
        return (
            <ExternalPartialPAPIColorChange
                toggle={<CardMenuDropdownToggle data-cy="color-change-toggle-dropdown" />}
                trigger="overview"
            />
        );
    }

    if (isAliasChangeCustomizationActive) {
        return (
            <ExternalPartialPAPIAliasChange
                toggle={<CardMenuDropdownToggle data-cy="name-change-toggle-dropdown" />}
                trigger="overview"
            />
        );
    }

    const onSelect = (action: DropdownEntry) => {
        if (action.value === 'CHANGE_IMAGE') {
            imageUploadInputRef.current?.click();
            return;
        }

        if (action.value === 'CHANGE_COLOR') {
            activateColorChangeCustomization();
            return;
        }

        if (action.value === 'CHANGE_NAME') {
            activateAliasChangeCustomization();
            return;
        }

        if (action.value === 'HIDE_BOX') {
            hideAccountOnOverview();
            return;
        }

        performEntryAction(action.value);
    };

    const renderToggle = () => (
        <CardMenuDropdownToggle data-cy="overview-card-secondary-menu-button" />
    );

    return (
        <>
            <OverviewCardMenu items={dropdownEntries} onSelect={onSelect} toggle={renderToggle} />
            <ExternalPartialPAPIImageChange
                ref={imageUploadInputRef}
                trigger="overview"
                productType={account.icon}
            />
        </>
    );
};

ActionsDropdownContent.wrapsComponent = OverviewCardMenu;
